import { Subscription } from 'rxjs';
import { FileUploadComponent, MAX_CERTIFICATES_COUNT } from './../file-upload/file-upload.component';
import { DeleteDialogData, DeleteItemDialogComponent } from '../../../dialogs/delete-item-dialog/delete-item-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { Component, OnInit, Input, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FileApiService } from 'src/app/data-transfer/services/file-api-service';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { CertificateDto } from 'src/app/data-transfer/entities/certificate-dto';
import { CertificateTypeDto } from 'src/app/data-transfer/entities/certificate-type-dto';

@Component({
  selector: 'app-certificate-upload',
  templateUrl: './certificate-upload.component.html',
  styleUrls: ['./certificate-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateUploadComponent extends FileUploadComponent implements OnInit, OnChanges, OnDestroy {

  @Input() certificates!: CertificateDto[];
  @Input() certificateTypes!: CertificateTypeDto[];

  newCertificates: File[] = [];

  private idSubscription?: Subscription;

  constructor(
    protected dialog: MatDialog,
    private translateService: TranslocoService,
    private fileApiService: FileApiService
  ) {
    super(dialog);
    this.minFieldWidth = 300;
    this.maxFieldWidth = 1000;
    super.setInitialNamesLength();
  }

  getCertificateType(certificateTypeId: number): string | undefined {
    return this.certificateTypes?.find(x => x.id === certificateTypeId)?.name;
  }

  ngOnInit(): void {
    super.setExistingNamesLength(this.certificates);
    this.maxCountReached = this.certificates.length >= MAX_CERTIFICATES_COUNT;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.certificates) {
      super.setExistingNamesLength(this.certificates);
      this.maxCountReached = this.certificates?.length >= MAX_CERTIFICATES_COUNT;
    }
  }

  onDeleteCertificateClick(certificate: CertificateDto) {
    if (!certificate) { return; }
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('certificate.deleteHeader'),
      dialogText: this.translateService.translate('certificate.deleteText')
    };
    const dialogRef = this.dialog.open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'));
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.DELETE) {
        this.deletePdf(certificate);
      }
    });
  }

  private deletePdf(certificate: CertificateDto) {
    if (!certificate) { return; }

    if (certificate.documentId === null) {
      this.newCertificates = this.newCertificates.filter(x => x.name !== certificate.name);
    }

    const index = this.certificates.findIndex(x => x.name === certificate.name);
    this.certificates.splice(index, 1);

    if (this.certificates.length < MAX_CERTIFICATES_COUNT) {
      this.maxCountReached = false;
    }
    super.setExistingNamesLength(this.certificates);
    this.filesEdited.emit(true);
  }

  onUploadPdfsClick(pdfFiles: FileList | null) {
    if (!this.certificates) { this.certificates = []; }
    if (pdfFiles && pdfFiles.length > 0) {
      for (const pdfFile of pdfFiles) {
        const fileType = pdfFile.type;
        if (fileType.match(/application\/pdf/) === null) {
          const dialogData = this.getInvalidPdfDialogData(pdfFile.name);
          this.showDialog(dialogData);
          continue;
        }
        if (this.certificates.length >= MAX_CERTIFICATES_COUNT) {
          this.maxCountReached = true;
          const dialogData = this.getMaxPdfsDialogData();
          this.showDialog(dialogData);
          return;
        }
        let assignedName = pdfFile.name;
        const listOfNames = this.certificates.map(x => x.name ?? "");
        if (listOfNames.includes(pdfFile.name)) {
          assignedName = this.appendTextToDuplicateNameRecursively(listOfNames, pdfFile.name, 0);
          const newFile = new File([pdfFile], assignedName, { type: pdfFile.type });
          this.newCertificates.push(newFile);
        } else {
          this.newCertificates.push(pdfFile);
        }
        const position = this.certificates.length;
        this.resizeInput(position);
        this.certificates.push({ documentId: undefined,
                                 index: position,
                                 name: assignedName,
                                 comment: '',
                                 validFrom: undefined,
                                 validUntil: undefined });
        this.maxCountReached = this.certificates.length >= MAX_CERTIFICATES_COUNT;
        this.filesEdited.emit(true);
      }
    }
  }

  onNameEditClicked(index: number) {
    super.onNameEdited(this.certificates, index, this.getDuplicatePdfNamesDialogData());
  }

  private getDuplicatePdfNamesDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.warning'),
      messages: [this.translateService.translate('warnings.duplicatePdfNames')], icon: 'warning'
    };
  }

  private getInvalidPdfDialogData(fileName: string): SimpleDialogData {
    return {
      title: fileName,
      messages: [this.translateService.translate('errors.formatNotSupported')], icon: 'error'
    };
  }

  private getMaxPdfsDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.information'),
      messages: [this.translateService.translate('errors.maxFilesReached')], icon: 'error'
    };
  }

  public getNewCertificatesFormData() {
    return super.getNewFilesFormData(this.newCertificates, 'files');
  }

  onViewPdfClick(certificate: CertificateDto) {
    console.log(this.newCertificates);
    let pdfUrl: string;
    if (!certificate.documentId) {
      const pdfFile = this.newCertificates.find(x => x.name === certificate.name);
      if (!pdfFile) { return; }
      pdfUrl = window.URL.createObjectURL(pdfFile);
      window.open(pdfUrl, '_blank');
    } else {
      this.idSubscription = this.fileApiService.getPdfById(certificate.documentId).subscribe(pdfFile => {
        pdfUrl = window.URL.createObjectURL(pdfFile);
        window.open(pdfUrl, '_blank');
      });
    }
  }

  ngOnDestroy(): void {
    this.idSubscription?.unsubscribe();
  }
}
